/**
 * DealActivityForm - Formulário para adicionar atividades ao Deal
 * Permite registrar notas, ligações, reuniões, etc.
 * Versão: 1.0.0
 */
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  FileText, 
  Phone, 
  Mail, 
  Calendar, 
  CheckSquare,
  Send
} from 'lucide-react';
import { useCrmDealActivities, ActivityType } from '@/hooks/useCrmDealActivities';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// Schema de validação
const activitySchema = z.object({
  activity_type: z.enum(['note', 'call', 'email', 'meeting', 'task'] as const),
  title: z.string().min(1, 'Título é obrigatório'),
  description: z.string().optional(),
  scheduled_at: z.string().optional(),
});

type ActivityFormData = z.infer<typeof activitySchema>;

// Props do componente
interface DealActivityFormProps {
  dealId: string;
  onSuccess?: () => void;
  compact?: boolean;
}

// Tipo manual para atividades que podem ser criadas pelo usuário
type ManualActivityType = 'note' | 'call' | 'email' | 'meeting' | 'task';

// Tipos de atividade disponíveis para criação manual
const activityTypes: { value: ManualActivityType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'note', label: 'Nota', icon: FileText },
  { value: 'call', label: 'Ligação', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Reunião', icon: Calendar },
  { value: 'task', label: 'Lembrete', icon: CheckSquare },
];

/**
 * Formulário para adicionar atividades
 */
export function DealActivityForm({ 
  dealId, 
  onSuccess,
  compact = false 
}: DealActivityFormProps) {
  // enabled:false → só usamos a mutation, não disparar SELECT * em crm_deal_activities
  const { createActivity } = useCrmDealActivities(dealId, { enabled: false });

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activity_type: 'note',
      title: '',
      description: '',
      scheduled_at: '',
    },
  });

  const selectedType = form.watch('activity_type');

  // Submete o formulário
  const onSubmit = async (data: ActivityFormData) => {
    await createActivity.mutateAsync({
      deal_id: dealId,
      activity_type: data.activity_type,
      title: data.title,
      description: data.description || undefined,
      // v1.0.1: Converte horário local para UTC antes de salvar
      scheduled_at: data.scheduled_at 
        ? new Date(data.scheduled_at).toISOString() 
        : undefined,
    });
    
    form.reset();
    onSuccess?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Seletor de tipo */}
        <div className="flex gap-1">
          {activityTypes.map(type => {
            const Icon = type.icon;
            const isSelected = selectedType === type.value;
            
            return (
              <Button
                key={type.value}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                className={cn(
                  'flex-1',
                  compact && 'px-2'
                )}
                onClick={() => form.setValue('activity_type', type.value)}
              >
                <Icon className="h-4 w-4" />
                {!compact && <span className="ml-1.5">{type.label}</span>}
              </Button>
            );
          })}
        </div>

        {/* Título */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título</FormLabel>
              <FormControl>
                <Input 
                  placeholder={`Título da ${activityTypes.find(t => t.value === selectedType)?.label.toLowerCase()}`}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Descrição */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição (opcional)</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Detalhes adicionais..."
                  className="resize-none"
                  rows={3}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Data agendada - apenas para meeting e task */}
        {(selectedType === 'meeting' || selectedType === 'task') && (
          <FormField
            control={form.control}
            name="scheduled_at"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data/Hora Agendada</FormLabel>
                <FormControl>
                  <Input 
                    type="datetime-local"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Botão de enviar */}
        <Button 
          type="submit" 
          className="w-full"
          disabled={createActivity.isPending}
        >
          {createActivity.isPending ? (
            'Salvando...'
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Adicionar Atividade
            </>
          )}
        </Button>
      </form>
    </Form>
  );
}
